import React, { useState, useEffect } from 'react';
import { AiOutlineWarning, AiOutlineClose } from 'react-icons/ai';
import { FaLock, FaChevronDown, FaChevronRight } from 'react-icons/fa';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

const UnauthorizedAccess = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availablePages, setAvailablePages] = useState([]);
  const [groupedPages, setGroupedPages] = useState({});
  const [selectedPage, setSelectedPage] = useState('');
  const [reason, setReason] = useState('');
  const [user, setUser] = useState({
    id: '',
    name: '',
    email: '',
    roles: [],
    currentRole: '',
  });
  const [apiMessage, setApiMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPagesLoading, setIsPagesLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Fetch current user data from access token
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
          credentials: 'include',
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            // Token expired or invalid - clear everything and redirect to login
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/login';
            return;
          }
          throw new Error('Failed to fetch user data');
        }

        const userData = await response.json();
        if (userData) {
          setUser({
            id: userData.id,
            name: `${userData.first_name} ${userData.last_name}`,
            email: userData.email,
            roles: userData.roles || [],
            currentRole: userData.roles?.[0] || '',
          });
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);

        // Clear everything and redirect to login on error
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch available pages when modal opens
  const fetchAvailablePages = async (email) => {
    setIsPagesLoading(true);
    try {
      const response = await fetch(
        `${AQ_BASE_URL}/aq/api/requestablePages?email=${encodeURIComponent(email)}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          window.location.href = '/login';
          return;
        }
        throw new Error('Failed to fetch available pages');
      }

      const result = await response.json();
      if (result.success) {
        setAvailablePages(result.pages || []);
        setGroupedPages(result.grouped_pages || {});

        // Auto-expand first group
        const groups = Object.keys(result.grouped_pages || {});
        if (groups.length > 0) {
          setExpandedGroups(new Set([groups[0]]));
        }
      } else {
        console.error('Failed to fetch available pages:', result.message);
      }
    } catch (error) {
      console.error('Error fetching available pages:', error);
    } finally {
      setIsPagesLoading(false);
    }
  };

  const handleRequestAccess = async () => {
    setIsModalOpen(true);
    // Fetch pages when modal opens
    if (user.email) {
      await fetchAvailablePages(user.email);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPage('');
    setReason('');
    setApiMessage('');
    setAvailablePages([]);
    setGroupedPages({});
    setExpandedGroups(new Set());
  };

  const toggleGroup = (groupName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const handlePageSelect = (pageId) => {
    setSelectedPage(pageId === selectedPage ? '' : pageId.toString());
  };

  const getSelectedPageName = () => {
    if (!selectedPage) return '';
    const page = availablePages.find((p) => p.id === parseInt(selectedPage));
    return page ? page.name : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPage) {
      setApiMessage('Please select a page to request access to.');
      return;
    }

    if (!reason.trim()) {
      setApiMessage('Please provide a reason for requesting access.');
      return;
    }

    if (!user.currentRole) {
      setApiMessage('Unable to determine your current role.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${AQ_BASE_URL}/aq/api/roleRequest?email=${encodeURIComponent(user.email)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            requested_permission_id: parseInt(selectedPage),
            reason: reason,
            current_role: user.currentRole,
          }),
        }
      );

      const result = await response.json();
      if (response.ok) {
        setApiMessage(result.message || 'Request submitted successfully.');

        // Remove the requested page from available pages
        const updatedPages = availablePages.filter((page) => page.id !== parseInt(selectedPage));
        setAvailablePages(updatedPages);

        // Also remove from grouped pages
        const updatedGroupedPages = { ...groupedPages };
        Object.keys(updatedGroupedPages).forEach((group) => {
          updatedGroupedPages[group] = updatedGroupedPages[group].filter(
            (page) => page.id !== parseInt(selectedPage)
          );
          // Remove empty groups
          if (updatedGroupedPages[group].length === 0) {
            delete updatedGroupedPages[group];
          }
        });
        setGroupedPages(updatedGroupedPages);

        // Clear selection after successful submission
        setSelectedPage('');
        setReason('');
        setTimeout(() => {
          closeModal();
        }, 3000);

        setTimeout(() => {
          if (updatedPages.length === 0) {
            // No more pages to request, close modal
            closeModal();
          } else {
            // Clear success message but keep modal open
            setApiMessage('');
          }
        }, 3000);
      } else {
        setApiMessage(result.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Request error:', error);
      setApiMessage('Failed to submit the request. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full p-4">
      {/* Main Access Denied Card */}
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-lg border border-gray-200">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-4">
            <AiOutlineWarning className="text-red-500 text-3xl" />
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>

          <p className="text-gray-600 mb-6">
            You don't have permission to access this page.
            {user.roles.length > 0 && (
              <span className="block mt-1 text-sm">
                Current role: <span className="font-medium">{user.roles.join(', ')}</span>
              </span>
            )}
          </p>

          <button
            onClick={handleRequestAccess}
            className="px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 transition-colors shadow-sm"
          >
            Request Page Access
          </button>

          <p className="text-xs text-gray-500 mt-4">
            Your request will be reviewed by an administrator.
          </p>
        </div>
      </div>

      {/* Request Access Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="mr-3 p-2 bg-gray-100 rounded-lg">
                    <FaLock className="text-gray-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Request Page Access</h3>
                    <p className="text-sm text-gray-500">
                      Select a page and provide a reason for access
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <AiOutlineClose className="text-gray-500 text-lg" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* User Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Name</p>
                    <p className="font-medium text-gray-800">{user.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="font-medium text-gray-800">{user.email}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-xs text-gray-500 mb-1">Current Role(s)</p>
                    <div className="flex flex-wrap gap-2">
                      {user.roles.map((role, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-200 text-gray-800 text-sm rounded-full font-medium"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Available Pages Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-800">Select a Page</h4>
                  {availablePages.length > 0 && (
                    <span className="text-xs text-gray-500">
                      {availablePages.length} page(s) available
                    </span>
                  )}
                </div>

                {isPagesLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Loading available pages...</p>
                  </div>
                ) : availablePages.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                    <AiOutlineWarning className="text-gray-400 text-2xl mx-auto mb-2" />
                    <p className="text-gray-500">No pages available to request</p>
                    <p className="text-sm text-gray-400 mt-1">
                      You already have access to all pages
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {Object.keys(groupedPages).length > 0 ? (
                      // Grouped view
                      Object.entries(groupedPages).map(([groupName, pages]) => (
                        <div
                          key={groupName}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleGroup(groupName)}
                            className="flex items-center justify-between w-full p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center">
                              <span className="font-medium text-gray-800">{groupName}</span>
                              <span className="ml-2 text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                {pages.length} page(s)
                              </span>
                            </div>
                            <span className="text-gray-500">
                              {expandedGroups.has(groupName) ? (
                                <FaChevronDown className="w-4 h-4" />
                              ) : (
                                <FaChevronRight className="w-4 h-4" />
                              )}
                            </span>
                          </button>

                          {expandedGroups.has(groupName) && (
                            <div className="border-t border-gray-200">
                              {pages.map((page) => (
                                <div
                                  key={page.id}
                                  onClick={() => handlePageSelect(page.id)}
                                  className={`p-4 border-b border-gray-100 last:border-b-0 cursor-pointer transition-all ${
                                    selectedPage === page.id.toString()
                                      ? 'bg-blue-50'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-start">
                                    <div
                                      className={`mt-1 mr-3 w-4 h-4 rounded-full border flex-shrink-0 ${
                                        selectedPage === page.id.toString()
                                          ? 'bg-blue-500 border-blue-500'
                                          : 'border-gray-300'
                                      }`}
                                    ></div>
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between">
                                        <span
                                          className={`font-medium ${
                                            selectedPage === page.id.toString()
                                              ? 'text-blue-700'
                                              : 'text-gray-800'
                                          }`}
                                        >
                                          {page.name}
                                        </span>
                                      </div>
                                      {page.description && (
                                        <p className="text-sm text-gray-600 mt-1">
                                          {page.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      // Flat view (fallback)
                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-60 overflow-y-auto">
                        {availablePages.map((page) => (
                          <div
                            key={page.id}
                            onClick={() => handlePageSelect(page.id)}
                            className={`p-4 cursor-pointer transition-all ${
                              selectedPage === page.id.toString()
                                ? 'bg-blue-50'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start">
                              <div
                                className={`mt-1 mr-3 w-4 h-4 rounded-full border flex-shrink-0 ${
                                  selectedPage === page.id.toString()
                                    ? 'bg-blue-500 border-blue-500'
                                    : 'border-gray-300'
                                }`}
                              ></div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span
                                    className={`font-medium ${
                                      selectedPage === page.id.toString()
                                        ? 'text-blue-700'
                                        : 'text-gray-800'
                                    }`}
                                  >
                                    {page.name}
                                  </span>
                                </div>
                                {page.description && (
                                  <p className="text-sm text-gray-600 mt-1">{page.description}</p>
                                )}
                                {page.parent && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Under: <span className="font-medium">{page.parent}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {selectedPage && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Selected:</span> {getSelectedPageName()}
                    </p>
                  </div>
                )}
              </div>

              {/* Reason Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Access *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg  placeholder-gray-400"
                  placeholder="Please explain why you need access to this page..."
                  rows={3}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your reason will help the admin understand your request.
                </p>
              </div>

              {/* API Message */}
              {apiMessage && (
                <div
                  className={`p-4 rounded-lg mb-4 ${
                    apiMessage.includes('successfully')
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  <div className="flex items-start">
                    <AiOutlineWarning
                      className={`mt-0.5 mr-2 flex-shrink-0 ${
                        apiMessage.includes('successfully') ? 'text-green-500' : 'text-red-500'
                      }`}
                    />
                    <p className="text-sm">{apiMessage}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isLoading}
                >
                  Cancel
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={
                    isLoading || !selectedPage || !reason.trim() || availablePages.length === 0
                  }
                  className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                    isLoading || !selectedPage || !reason.trim() || availablePages.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-800 text-white hover:bg-gray-900'
                  }`}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnauthorizedAccess;

// import React, { useState, useEffect } from "react";
// import { AiOutlineWarning, AiOutlineClose } from "react-icons/ai";
// import { FaLock } from "react-icons/fa";

// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

// const UnauthorizedAccess = () => {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [roles, setRoles] = useState([]); // Dynamic roles
//   const [role, setRole] = useState("");
//   const [reason, setReason] = useState("");
//   const [user, setUser] = useState({
//     name: "",
//     email: "",
//   });
//   const [apiMessage, setApiMessage] = useState(""); // API response message
//   const [isLoading, setIsLoading] = useState(false); // Loading state
//   const [isRolesLoading, setIsRolesLoading] = useState(true); // Loading state for roles

//   // Get user data from localStorage when component mounts
//   useEffect(() => {
//     const userData = JSON.parse(localStorage.getItem("user"));
//     if (userData) {
//       setUser({
//         name: userData.name || "",
//         email: userData.email || "",
//       });
//     }
//   }, []);
//   useEffect(() => {
//     const fetchRoles = async () => {
//       if (!user.email) return; // Wait until email is available

//       try {
//         const response = await fetch(
//           `${AQ_BASE_URL}/aq/api/roles/getAllRoles?email=${encodeURIComponent(
//             user.email
//           )}`
//         );
//         const result = await response.json();
//         if (response.ok) {
//           setRoles(result.map((role) => role.role_name)); // Extract role names
//         } else {
//           console.error("Failed to fetch roles:", result.message);
//         }
//       } catch (error) {
//         console.error("Error fetching roles:", error);
//       } finally {
//         setIsRolesLoading(false); // Optional: you had a loading state
//       }
//     };

//     fetchRoles();
//   }, [user.email]); // Depend on user.email so it waits until available

//   const handleRequestAccess = () => {
//     setIsModalOpen(true);
//   };

//   const closeModal = () => {
//     setIsModalOpen(false);
//     setReason(""); // Clear reason input
//     setApiMessage(""); // Clear previous API messages
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!role || !reason) {
//       setApiMessage("Please select a role and provide a reason.");
//       return;
//     }

//     setIsLoading(true); // Show loading indicator
//     try {
//       const response = await fetch(
//         `${AQ_BASE_URL}/aq/api/roleRequest?email=${encodeURIComponent(
//           user.email
//         )}`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Accept: "application/json",
//           },
//           body: JSON.stringify({
//             requested_role: role,
//             reason: reason,
//           }),
//         }
//       );

//       const result = await response.json();
//       if (response.ok) {
//         setApiMessage(result.message || "Request submitted successfully.");

//         setTimeout(() => {
//           setIsModalOpen(false);
//           closeModal();
//         }, 3000);

//       } else {
//         setApiMessage(
//           result.detail || "Something went wrong. Please try again."
//         );
//       }
//     } catch (error) {
//       setApiMessage(
//         "Failed to submit the request. Please check your connection."
//       );
//     } finally {
//       setIsLoading(false); // Hide loading indicator
//     }
//   };

//   return (
//     <div className="flex items-center justify-center h-full p-4">
//       <div className="bg-white p-8 border text-center w-full max-w-md rounded-md">
//         <h2 className="flex items-center justify-center text-2xl font-semibold mb-4 text-red-500">
//           <AiOutlineWarning className="text-red-500 text-3xl mr-3" />
//           Access Denied
//         </h2>
//         <p className="text-sm mb-6 text-gray-400">
//           You are not authorized to view this page. Please contact an
//           administrator to get authorized.
//         </p>
//         <button
//           onClick={handleRequestAccess}
//           className="bg-cyan-500 text-white px-4 py-2 rounded-sm hover:text-black"
//         >
//           Request Access
//         </button>
//       </div>

//       {/* Modal */}
//       {isModalOpen && (
//         <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-80 z-50">
//           <div className="bg-white p-6 rounded-md shadow-lg w-1/3 relative">
//             <AiOutlineClose
//               onClick={closeModal}
//               className="absolute top-4 right-6 text-xl text-red-500 cursor-pointer"
//             />
//             <div className="flex items-center justify-center">
//               <h3 className="text-lg font-semibold text-center flex items-center">
//                 <span className="mr-2 text-red-500">
//                   <FaLock size={16} />
//                 </span>
//                 Request Access
//               </h3>
//             </div>
//             <p className="text-xs text-center text-gray-500">
//               Once you submit your request, it will be sent to the admin for
//               approval.
//             </p>
//             <form className="space-y-3 mt-4" onSubmit={handleSubmit}>
//               <div>
//                 <label className="block text-xs text-black">Name</label>
//                 <input
//                   type="text"
//                   value={user.name}
//                   className="w-full px-2 py-2 text-xs border border-gray-300 rounded-md placeholder:text-black outline-none cursor-not-allowed"
//                   readOnly
//                   disabled
//                 />
//               </div>

//               <div>
//                 <label className="block text-xs text-black">Email</label>
//                 <input
//                   type="email"
//                   value={user.email}
//                   className="w-full px-2 py-2 text-xs border border-gray-300 rounded-md placeholder:text-black outline-none cursor-not-allowed"
//                   readOnly
//                   disabled
//                 />
//               </div>

//               <div>
//                 <label className="block text-xs text-black">Role</label>
//                 <select
//                   value={role}
//                   onChange={(e) => setRole(e.target.value)}
//                   className="w-full px-2 py-2 text-xs border border-gray-300 rounded-md"
//                   disabled={isRolesLoading}
//                 >
//                   <option value="">Select a role</option>
//                   {roles.map((roleOption, index) => (
//                     <option key={index} value={roleOption}>
//                       {roleOption}
//                     </option>
//                   ))}
//                 </select>
//                 {isRolesLoading && (
//                   <p className="text-xs text-gray-500 mt-1">Loading roles...</p>
//                 )}
//               </div>

//               <div>
//                 <label className="block text-xs text-black">Reason</label>
//                 <textarea
//                   value={reason}
//                   onChange={(e) => setReason(e.target.value)}
//                   className="w-full px-2 py-2 text-xs border border-gray-300 rounded-md"
//                   placeholder="Enter your reason for requesting this role."
//                 />
//               </div>

//               {apiMessage && (
//                 <p
//                   className={`text-xs text-center mt-2 ${
//                     apiMessage.includes("submitted")
//                       ? "text-green-500"
//                       : "text-red-500"
//                   }`}
//                 >
//                   {apiMessage}
//                 </p>
//               )}

//               <div className="mt-4 flex items-center justify-center">
//                 <button
//                   type="submit"
//                   className="bg-cyan-500 text-white px-4 py-2 rounded-lg hover:text-black"
//                   disabled={isLoading}
//                 >
//                   {isLoading ? "Submitting..." : "Submit Request"}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default UnauthorizedAccess;
